import {
  CheckCircleOutlined,
  CheckOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Space } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CustomizeRail,
  RailHint,
  RailLabel,
  SaveIndicator,
  ToolbarRow,
} from './DashboardToolbar.style';
import type { SaveStatus } from './persistence/useDashboardPersistence';

const RAIL_LABEL_ID = 'dashboard-customize-rail-label';

type DashboardToolbarProps = {
  isEditing: boolean;
  saveStatus: SaveStatus;
  /** Number of widgets not yet on the dashboard, surfaced on the Add button. */
  addableWidgetCount: number;
  disabled?: boolean;
  onToggleEdit: (editing: boolean) => void;
  onAddClick: () => void;
};

// One persistent live region whose CONTENT changes with status. A region that is
// mounted/unmounted per status (or returns null when idle) is announced
// unreliably by screen readers; keeping the node and swapping its text fixes that.
function SaveStatusLabel({ status }: { status: SaveStatus }) {
  const { t } = useTranslation();

  return (
    <SaveIndicator>
      {status === 'saving' && (
        <>
          <LoadingOutlined /> {t('components.saving', 'Saving…')}
        </>
      )}
      {status === 'saved' && (
        <>
          <CheckCircleOutlined /> {t('components.saved', 'Saved')}
        </>
      )}
      {status === 'error' && (
        <>
          <ExclamationCircleOutlined /> {t('components.saveError', 'Couldn’t save')}
        </>
      )}
    </SaveIndicator>
  );
}

/** An Edit button; in Customize mode it becomes the rail with the edit actions and Done. */
export function DashboardToolbar({
  isEditing,
  saveStatus,
  addableWidgetCount,
  disabled = false,
  onToggleEdit,
  onAddClick,
}: DashboardToolbarProps) {
  const { t } = useTranslation();
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const doneButtonRef = useRef<HTMLButtonElement>(null);
  const shouldMoveFocusRef = useRef(false);

  // Edit and Done replace each other, so the clicked button unmounts. Hand focus to
  // its counterpart instead of letting it drop to <body>.
  useEffect(() => {
    if (!shouldMoveFocusRef.current) {
      return;
    }
    shouldMoveFocusRef.current = false;
    (isEditing ? doneButtonRef : editButtonRef).current?.focus();
  }, [isEditing]);

  const handleToggleEdit = (editing: boolean) => {
    shouldMoveFocusRef.current = true;
    onToggleEdit(editing);
  };

  if (!isEditing) {
    return (
      <ToolbarRow>
        <Button
          ref={editButtonRef}
          icon={<EditOutlined />}
          disabled={disabled}
          onClick={() => handleToggleEdit(true)}
          data-testid="dashboard-edit"
        >
          {t('components.edit', 'Edit')}
        </Button>
      </ToolbarRow>
    );
  }

  return (
    <CustomizeRail
      role="region"
      aria-labelledby={RAIL_LABEL_ID}
      data-testid="dashboard-customize-rail"
    >
      <RailLabel>
        <EditOutlined />
        <span>
          <strong id={RAIL_LABEL_ID}>{t('components.customizeMode', 'Customize mode.')}</strong>{' '}
          <RailHint>
            {t(
              'components.customizeModeBannerBody',
              "Drag a tile's header to move it, drag its corner to resize, and use Add widget. Changes save automatically."
            )}
          </RailHint>
        </span>
      </RailLabel>
      <SaveStatusLabel status={saveStatus} />
      <Space>
        <Button icon={<PlusOutlined />} onClick={onAddClick} data-testid="dashboard-add-widget">
          {addableWidgetCount > 0
            ? t('components.addWidgetWithCount', 'Add widget · {{widgetCount}}', {
                widgetCount: addableWidgetCount,
              })
            : t('components.addWidget', 'Add widget')}
        </Button>
        <Button
          ref={doneButtonRef}
          icon={<CheckOutlined />}
          onClick={() => handleToggleEdit(false)}
          data-testid="dashboard-done"
        >
          {t('components.done', 'Done')}
        </Button>
      </Space>
    </CustomizeRail>
  );
}
